import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

const SITE_CONTROLS_KEY = 'mm_site_controls_v1'

export const SITE_CONTROL_DEFAULTS = {
  maintenanceMode: false,
  enableLoan: true,
  enableBonds: true,
  enableTrade: true,
  enableAssets: true,
  enableGold: true,
  enableAuction: false,
  enableSafe: false,
  enableFracture: false,
  enableCds: false,
  enableTrustFund: false,
  enableCredentials: true,
  enableSimulation: true,
  enableAnalytics: true,
  enableMarket: true,
  enableDocumentation: false,
} as const

export type SiteControlState = { [K in keyof typeof SITE_CONTROL_DEFAULTS]: boolean }
export type SiteControlKey = keyof SiteControlState

const V1_FORCED_DISABLED_CONTROLS = {
  enableAuction: false,
  enableSafe: false,
  enableFracture: false,
  enableCds: false,
  enableTrustFund: false,
  enableDocumentation: false,
} as const

function applyV1ReleaseScope(controls: SiteControlState): SiteControlState {
  return {
    ...controls,
    ...V1_FORCED_DISABLED_CONTROLS,
  }
}

function normalizeControls(value: unknown): SiteControlState {
  if (!value || typeof value !== 'object') {
    return { ...SITE_CONTROL_DEFAULTS }
  }

  const input = value as Partial<Record<SiteControlKey | 'enableContracts', unknown>>

  return applyV1ReleaseScope({
    maintenanceMode: Boolean(input.maintenanceMode ?? SITE_CONTROL_DEFAULTS.maintenanceMode),
    enableLoan: Boolean(input.enableLoan ?? input.enableContracts ?? SITE_CONTROL_DEFAULTS.enableLoan),
    enableBonds: Boolean(input.enableBonds ?? input.enableContracts ?? SITE_CONTROL_DEFAULTS.enableBonds),
    enableTrade: Boolean(input.enableTrade ?? input.enableContracts ?? SITE_CONTROL_DEFAULTS.enableTrade),
    enableAssets: Boolean(input.enableAssets ?? input.enableContracts ?? SITE_CONTROL_DEFAULTS.enableAssets),
    enableGold: Boolean(input.enableGold ?? input.enableContracts ?? SITE_CONTROL_DEFAULTS.enableGold),
    enableAuction: Boolean(input.enableAuction ?? input.enableContracts ?? SITE_CONTROL_DEFAULTS.enableAuction),
    enableSafe: Boolean(input.enableSafe ?? input.enableContracts ?? SITE_CONTROL_DEFAULTS.enableSafe),
    enableFracture: Boolean(input.enableFracture ?? input.enableContracts ?? SITE_CONTROL_DEFAULTS.enableFracture),
    enableCds: Boolean(input.enableCds ?? input.enableContracts ?? SITE_CONTROL_DEFAULTS.enableCds),
    enableTrustFund: Boolean(input.enableTrustFund ?? input.enableContracts ?? SITE_CONTROL_DEFAULTS.enableTrustFund),
    enableCredentials: Boolean(input.enableCredentials ?? SITE_CONTROL_DEFAULTS.enableCredentials),
    enableSimulation: Boolean(input.enableSimulation ?? SITE_CONTROL_DEFAULTS.enableSimulation),
    enableAnalytics: Boolean(input.enableAnalytics ?? SITE_CONTROL_DEFAULTS.enableAnalytics),
    enableMarket: Boolean(input.enableMarket ?? SITE_CONTROL_DEFAULTS.enableMarket),
    enableDocumentation: Boolean(input.enableDocumentation ?? SITE_CONTROL_DEFAULTS.enableDocumentation),
  })
}

export function readStoredSiteControls(): SiteControlState {
  if (typeof window === 'undefined') {
    return { ...SITE_CONTROL_DEFAULTS }
  }

  try {
    const raw = localStorage.getItem(SITE_CONTROLS_KEY)
    if (!raw) return { ...SITE_CONTROL_DEFAULTS }
    return normalizeControls(JSON.parse(raw))
  } catch {
    return { ...SITE_CONTROL_DEFAULTS }
  }
}

export const useSiteControlsStore = defineStore('site-controls', () => {
  const controls = ref<SiteControlState>(readStoredSiteControls())

  function persist() {
    if (typeof window === 'undefined') return
    localStorage.setItem(SITE_CONTROLS_KEY, JSON.stringify(controls.value))
  }

  function setControl(key: SiteControlKey, value: boolean) {
    controls.value = applyV1ReleaseScope({
      ...controls.value,
      [key]: value,
    })
    persist()
  }

  function setControls(next: Partial<SiteControlState>) {
    controls.value = normalizeControls({
      ...controls.value,
      ...next,
    })
    persist()
  }

  function resetControls() {
    controls.value = applyV1ReleaseScope({ ...SITE_CONTROL_DEFAULTS })
    persist()
  }

  const enabledCount = computed(() =>
    Object.entries(controls.value).filter(([key, value]) => key !== 'maintenanceMode' && value).length,
  )

  return {
    controls,
    enabledCount,
    setControl,
    setControls,
    resetControls,
  }
})
